import { useState, useCallback } from 'react';
import { requestUploadToken, uploadToPresignedUrl } from './unstructured.api';
import type { ResourceType, ResourceSubType } from './unstructured.types';

export interface UseUnstructuredUploadOptions {
  resourceType: ResourceType;
  subType: ResourceSubType;
  contentType: string;
  /** 업로드 전 파일 검증. 에러 메시지 반환 시 업로드 중단. */
  validateFile?: (file: File) => string | null;
  defaultErrorMessage?: string;
}

export interface UseUnstructuredUploadResult {
  /** 파일 업로드 후 structuredToken 반환 */
  uploadFile: (file: File) => Promise<string>;
  isUploading: boolean;
  error: string | null;
  resetError: () => void;
}

export function useUnstructuredUpload({
  resourceType,
  subType,
  contentType,
  validateFile,
  defaultErrorMessage = '파일 업로드에 실패했습니다.',
}: UseUnstructuredUploadOptions): UseUnstructuredUploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetError = useCallback(() => setError(null), []);

  const uploadFile = useCallback(
    async (file: File): Promise<string> => {
      if (validateFile) {
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          throw new Error(validationError);
        }
      }

      setError(null);
      setIsUploading(true);
      try {
        const { structuredToken, uploadUrl } = await requestUploadToken({
          resourceType,
          subType,
          contentType,
        });
        await uploadToPresignedUrl(uploadUrl, file, contentType);
        return structuredToken;
      } catch (err) {
        const message = err instanceof Error ? err.message : defaultErrorMessage;
        setError(message);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [resourceType, subType, contentType, validateFile, defaultErrorMessage]
  );

  return { uploadFile, isUploading, error, resetError };
}
